import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { api } from '../services/api';
import { ArrowLeft, Copy, Edit, Trash2, Clock, Share2, Download, Eye, Users } from 'lucide-react';
import toast from 'react-hot-toast';
import ShareModal from '../components/ShareModal';
import ShareManagement from '../components/ShareManagement';
import './KubeconfigDetail.css';

interface Kubeconfig {
  id: string;
  name: string;
  description?: string;
  clusterName?: string;
  contextName?: string;
  tags: string[];
  notes?: string;
  createdAt: string;
  updatedAt: string;
  createdBy: {
    id: string;
    username: string;
    firstName?: string;
    lastName?: string;
  };
  permissions: {
    canView: boolean;
    canEdit: boolean;
    canDelete: boolean;
  };
  data?: {
    content: string;
  };
}

const KubeconfigDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [showShareModal, setShowShareModal] = useState(false);
  const [showShareManagement, setShowShareManagement] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingKubeconfig, setEditingKubeconfig] = useState<Kubeconfig | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    content: '',
    tags: [] as string[],
    notes: '',
  });
  const [uploadFile, setUploadFile] = useState<File | null>(null);

  const { data, isLoading } = useQuery(
    ['kubeconfig', id],
    async () => {
      const response = await api.get(`/kubeconfig/${id}`);
      return response.data;
    },
    { enabled: !!id }
  );

  const deleteMutation = useMutation(
    async () => {
      await api.delete(`/kubeconfig/${id}`);
    },
    {
      onSuccess: () => {
        toast.success('Kubeconfig deleted successfully');
        navigate('/kubeconfig');
      },
      onError: (error: any) => {
        toast.error(error.response?.data?.error || 'Failed to delete kubeconfig');
      },
    }
  );

  const kubeconfig: Kubeconfig = data;

  const handleCopy = async (text: string, label: string) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success(`${label} copied to clipboard`);
      setTimeout(async () => {
        await navigator.clipboard.writeText('');
      }, 30000);
    } catch (error) {
      toast.error('Failed to copy to clipboard');
    }
  };

  const handleDownload = async () => {
    if (!kubeconfig?.data?.content) return;
    
    try {
      const blob = new Blob([kubeconfig.data.content], { type: 'application/x-yaml' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${kubeconfig.name.replace(/[^a-z0-9]/gi, '_')}_kubeconfig.yaml`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      toast.success('Kubeconfig downloaded successfully');
    } catch (error) {
      toast.error('Failed to download kubeconfig');
    }
  };

  const handleDelete = () => {
    if (window.confirm('Are you sure you want to delete this kubeconfig?')) {
      deleteMutation.mutate();
    }
  };

  const handleEdit = () => {
    if (!kubeconfig) return;
    setEditingKubeconfig(kubeconfig);
    setFormData({
      name: kubeconfig.name,
      description: kubeconfig.description || '',
      content: '',
      tags: kubeconfig.tags || [],
      notes: kubeconfig.notes || '',
    });
    setUploadFile(null);
    setShowEditModal(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!id) return;

    try {
      const formDataToSend = new FormData();
      formDataToSend.append('name', formData.name);
      if (formData.description) formDataToSend.append('description', formData.description);
      if (formData.tags.length > 0) formDataToSend.append('tags', JSON.stringify(formData.tags));
      if (formData.notes) formDataToSend.append('notes', formData.notes);

      if (uploadFile) {
        formDataToSend.append('file', uploadFile);
      } else if (formData.content) {
        formDataToSend.append('content', formData.content);
      }

      const formDataObj: any = {
        name: formData.name,
      };
      if (formData.description) formDataObj.description = formData.description;
      if (formData.notes) formDataObj.notes = formData.notes;
      if (formData.tags.length > 0) formDataObj.tags = formData.tags;
      if (formData.content) formDataObj.content = formData.content;

      // Use FormData for file upload, otherwise use JSON
      if (uploadFile) {
        const formDataToSend = new FormData();
        Object.keys(formDataObj).forEach(key => {
          if (formDataObj[key] !== undefined) {
            if (Array.isArray(formDataObj[key])) {
              formDataToSend.append(key, JSON.stringify(formDataObj[key]));
            } else {
              formDataToSend.append(key, formDataObj[key]);
            }
          }
        });
        formDataToSend.append('file', uploadFile);
        
        const response = await fetch(`${process.env.REACT_APP_API_URL || '/api'}/kubeconfig/${id}`, {
          method: 'PUT',
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`,
          },
          body: formDataToSend,
        });

        if (response.ok) {
          toast.success('Kubeconfig updated successfully');
          setShowEditModal(false);
          queryClient.invalidateQueries(['kubeconfig', id]);
        } else {
          const errorData = await response.json();
          toast.error(errorData.error || 'Failed to update kubeconfig');
        }
      } else if (formData.content) {
        const formDataToSend = new FormData();
        Object.keys(formDataObj).forEach(key => {
          if (formDataObj[key] !== undefined) {
            if (Array.isArray(formDataObj[key])) {
              formDataToSend.append(key, JSON.stringify(formDataObj[key]));
            } else {
              formDataToSend.append(key, formDataObj[key]);
            }
          }
        });
        
        const response = await fetch(`${process.env.REACT_APP_API_URL || '/api'}/kubeconfig/${id}`, {
          method: 'PUT',
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`,
          },
          body: formDataToSend,
        });

        if (response.ok) {
          toast.success('Kubeconfig updated successfully');
          setShowEditModal(false);
          queryClient.invalidateQueries(['kubeconfig', id]);
        } else {
          const errorData = await response.json();
          toast.error(errorData.error || 'Failed to update kubeconfig');
        }
      } else {
        // Only metadata update, no content change
        try {
          await api.put(`/kubeconfig/${id}`, formDataObj);
          toast.success('Kubeconfig updated successfully');
          setShowEditModal(false);
          queryClient.invalidateQueries(['kubeconfig', id]);
        } catch (error: any) {
          toast.error(error.response?.data?.error || 'Failed to update kubeconfig');
        }
      }
    } catch (error: any) {
      toast.error(error.message || 'Failed to update kubeconfig');
    }
  };

  if (isLoading) {
    return <div className="loading">Loading kubeconfig...</div>;
  }

  if (!kubeconfig) {
    return (
      <div className="error-state">
        <p>Kubeconfig not found</p>
        <Link to="/kubeconfig" className="btn-primary">
          <ArrowLeft size={20} />
          <span>Back to Kubeconfigs</span>
        </Link>
      </div>
    );
  }

  return (
    <div className="kubeconfig-detail">
      <div className="detail-header">
        <Link to="/kubeconfig" className="back-link">
          <ArrowLeft size={20} />
          <span>Back</span>
        </Link>
        <div className="header-actions">
          {kubeconfig.permissions.canView && (
            <>
              <button className="btn-secondary" onClick={() => setShowShareManagement(true)}>
                <Users size={20} />
                <span>Manage Shares</span>
              </button>
              <button className="btn-secondary" onClick={handleDownload}>
                <Download size={20} />
                <span>Download</span>
              </button>
              <button className="btn-primary" onClick={() => setShowShareModal(true)}>
                <Share2 size={20} />
                <span>Share</span>
              </button>
            </>
          )}
          {kubeconfig.permissions.canEdit && (
            <button className="btn-secondary" onClick={handleEdit}>
              <Edit size={20} />
              <span>Edit</span>
            </button>
          )}
          {kubeconfig.permissions.canDelete && (
            <button className="btn-danger" onClick={handleDelete}>
              <Trash2 size={20} />
              <span>Delete</span>
            </button>
          )}
        </div>
      </div>

      <div className="detail-content">
        <div className="detail-main">
          <div className="detail-card">
            <div className="card-header">
              <h1>{kubeconfig.name}</h1>
              <div className="badges">
                {kubeconfig.clusterName && (
                  <span className="badge">Cluster: {kubeconfig.clusterName}</span>
                )}
                {kubeconfig.contextName && (
                  <span className="badge">Context: {kubeconfig.contextName}</span>
                )}
                <span className="badge">Kubeconfig</span>
              </div>
            </div>

            {kubeconfig.description && (
              <div className="detail-section">
                <h3>Description</h3>
                <p>{kubeconfig.description}</p>
              </div>
            )}

            {kubeconfig.data && kubeconfig.permissions.canView && (
              <div className="detail-section">
                <h3>Kubeconfig Content</h3>
                <div className="kubeconfig-content-wrapper">
                  <textarea
                    readOnly
                    value={kubeconfig.data.content}
                    rows={20}
                    className="kubeconfig-content"
                  />
                  <div className="content-actions">
                    <button
                      className="btn-secondary btn-sm"
                      onClick={() => handleCopy(kubeconfig.data!.content, 'Kubeconfig')}
                    >
                      <Copy size={16} />
                      <span>Copy Content</span>
                    </button>
                    <button
                      className="btn-secondary btn-sm"
                      onClick={handleDownload}
                    >
                      <Download size={16} />
                      <span>Download</span>
                    </button>
                  </div>
                </div>
              </div>
            )}

            {kubeconfig.tags && kubeconfig.tags.length > 0 && (
              <div className="detail-section">
                <h3>Tags</h3>
                <div className="tags">
                  {kubeconfig.tags.map((tag, idx) => (
                    <span key={idx} className="tag">
                      {tag}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {kubeconfig.notes && (
              <div className="detail-section">
                <h3>Notes</h3>
                <p className="notes">{kubeconfig.notes}</p>
              </div>
            )}

            <div className="detail-meta">
              <div className="meta-item">
                <label>Created By</label>
                <span>{kubeconfig.createdBy.username}</span>
              </div>
              <div className="meta-item">
                <label>Created At</label>
                <span>{new Date(kubeconfig.createdAt).toLocaleString()}</span>
              </div>
              <div className="meta-item">
                <label>Last Updated</label>
                <span>{new Date(kubeconfig.updatedAt).toLocaleString()}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {showEditModal && editingKubeconfig && (
        <div className="modal-overlay" onClick={() => setShowEditModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h2>Edit Kubeconfig</h2>
            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label>Name *</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                />
              </div>
              <div className="form-group">
                <label>Description</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  rows={3}
                />
              </div>
              <div className="form-group">
                <label>Update Content (Optional)</label>
                <input
                  type="file"
                  accept=".yaml,.yml,.json"
                  onChange={(e) => {
                    if (e.target.files && e.target.files[0]) {
                      setUploadFile(e.target.files[0]);
                      setFormData({ ...formData, content: '' });
                    }
                  }}
                />
                {!uploadFile && (
                  <textarea
                    placeholder="Or paste kubeconfig content here..."
                    value={formData.content}
                    onChange={(e) => {
                      setFormData({ ...formData, content: e.target.value });
                      setUploadFile(null);
                    }}
                    rows={10}
                    style={{ marginTop: '10px', fontFamily: 'monospace' }}
                  />
                )}
              </div>
              <div className="form-group">
                <label>Notes</label>
                <textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  rows={3}
                />
              </div>
              <div className="form-actions">
                <button type="button" className="btn-secondary" onClick={() => setShowEditModal(false)}>
                  Cancel
                </button>
                <button type="submit" className="btn-primary">
                  Update
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showShareManagement && (
        <div className="modal-overlay" onClick={() => setShowShareManagement(false)}>
          <div className="modal-content share-management-modal" onClick={(e) => e.stopPropagation()}>
            <ShareManagement
              kubeconfigId={kubeconfig.id}
              resourceType="kubeconfig"
              onClose={() => setShowShareManagement(false)}
            />
          </div>
        </div>
      )}

      {showShareModal && kubeconfig && (
        <ShareModal
          kubeconfigId={kubeconfig.id}
          kubeconfigName={kubeconfig.name}
          resourceType="kubeconfig"
          onClose={() => setShowShareModal(false)}
          onSuccess={() => {
            setShowShareModal(false);
            queryClient.invalidateQueries(['kubeconfig', id]);
          }}
        />
      )}
    </div>
  );
};

export default KubeconfigDetail;

